import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import Hidden from '@/components/ui/TzoHidden';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form990vsForm990EZ = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="Understand the Difference Between Form 990-N vs Form 990-EZ"
          description="Understand the key differences between Form 990-N and Form 990-EZ. Learn which one suits your nonprofit and e-file quickly with TaxZerone today."
          keywords=""
          canonical="resources/tax-exempt/form-990-n-vs-form-990-ez/"
          ogType="article"
          jsonLdPricing="89.99"
          jsonLdAggregateRatingValue="4.99"
          jsonLdAggregateRatingCount="2063"
        />
      }
    >
      <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 3,
            mt: 5,
            textAlign: 'center',
          }}
        >
          {' '}
          <span className="highlight-container">
            <span className="highlight">Form 990-N </span>
          </span>{' '}
          vs{' '}
          <span className="highlight-container">
            <span className="highlight"> Form 990-EZ: </span>
          </span>{' '}
          Key Differences for
          <br /> Small Tax-Exempt Organizations
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            mt: 1,
            mb: 2,
          }}
        >
          Discover which IRS 990 forms best suits your nonprofit’s needs—with
          TaxZerone!
        </Typography>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            variant="contained"
            className="btn-primary medium"
            sx={{
              fontSize: {
                xs: '16px !important',
                sm: '18px !important',
                lg: '24px !important',
              },
              transition: 'all 0.3s ease 0s',
              '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
            }}
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=990Nvs990EZ`}
          >
            E-file Now!
          </Button>
        </Box>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/tax-exempt/form-990-n-vs-form-990-ez/"
                  />

                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-07-20T09:00:00-05:00"
                      >
                        July 20, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-07-20T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        July 20, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{
                            color: 'inherit',
                            textDecoration: 'none',
                          }}
                        >
                          Peter Samuel
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form 990-N vs. Form 990-EZ at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemScope
                    itemType="https://schema.org/ItemList"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="1" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>Purpose:</strong>{' '}
                        Compare{' '}
                        <Link
                          href="https://epostcard.taxzerone.com/?ref=menu&_gl=1*1vfwdlz*_gcl_au*ODg5ODU2MDY2LjE3ODQwOTMzNTg."
                          sx={{
                            color: '#0202ff',
                            textDecoration: 'underline',
                          }}
                        >
                          IRS Form 990-N
                        </Link>{' '}
                        <strong style={{ fontWeight: 600 }}>
                          (e-Postcard)
                        </strong>{' '}
                        and{' '}
                        <Link
                          href="https://www.taxzerone.com/tax-exempt/e-file-irs-form-990-ez/"
                          sx={{
                            color: '#0202ff',
                            textDecoration: 'underline',
                          }}
                        >
                          IRS Form 990-EZ
                        </Link>{' '}
                        to determine which annual return best fits your
                        organization's size and filing requirements.
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="2" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          Who Files Form 990-N?
                        </strong>{' '}
                        Organizations with annual gross receipts normally{' '}
                        <strong style={{ fontWeight: 600 }}>
                          $50,000 or less
                        </strong>{' '}
                        generally file{' '}
                        <Link
                          href="https://epostcard.taxzerone.com/?ref=menu&_gl=1*j7qmh4*_gcl_au*ODg5ODU2MDY2LjE3ODQwOTMzNTg."
                          sx={{
                            color: '#0202ff',
                            textDecoration: 'underline',
                          }}
                        >
                          Form 990-N
                        </Link>{' '}
                        (e-Postcard), which requires only basic organizational
                        information and is filed electronically.
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="3" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          Who Files Form 990-EZ?
                        </strong>{' '}
                        Organizations with gross receipts greater than{' '}
                        <strong style={{ fontWeight: 600 }}>
                          $50,000 but less than $200,000
                        </strong>{' '}
                        and total assets under{' '}
                        <strong style={{ fontWeight: 600 }}>$500,000</strong>{' '}
                        generally{' '}
                        <Link
                          href="https://www.taxzerone.com/tax-exempt/e-file-irs-form-990-ez/"
                          sx={{
                            color: '#0202ff',
                            textDecoration: 'underline',
                          }}
                        >
                          file Form 990-EZ
                        </Link>
                        , which requires financial statements and additional
                        reporting.
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="4" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          Key Difference:
                        </strong>{' '}
                        Form 990-N is a simple electronic notice with no
                        financial reporting, while Form 990-EZ is a short-form
                        annual return that requires financial and operational
                        information.
                      </span>
                    </li>
                  </Box>

                  <div
                    itemProp="publisher"
                    itemScope
                    itemType="https://schema.org/Organization"
                    style={{ display: 'none' }}
                  >
                    <span itemProp="name">TaxZerone</span>

                    <div
                      itemProp="logo"
                      itemScope
                      itemType="https://schema.org/ImageObject"
                    >
                      <link
                        itemProp="url"
                        href="https://www.taxzerone.com/assets/images/logo.webp"
                      />
                      <data itemProp="width" value="380" />
                      <data itemProp="height" value="124" />
                    </div>
                  </div>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Nonprofit organizations must file an annual return with the
                  IRS to preserve their tax-exempt status. Depending on the
                  organization’s size and financial activity, it may need to
                  file either{' '}
                  <Link
                    href={`${process.env.NEXT_PUBLIC_990N_SITE_URL}?ref=990vs990ez`}
                    sx={{ color: '#0202ff' }}
                  >
                    Form 990-N
                  </Link>{' '}
                  or{' '}
                  <Link
                    href="/tax-exempt/e-file-irs-form-990-ez/"
                    sx={{ color: '#0202ff' }}
                  >
                    Form 990-EZ.
                  </Link>{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  This page will help you understand the key differences between
                  these forms, including eligibility criteria, filing
                  requirements, and the information required.
                </Typography>
                <Box
                  sx={{
                    pt: 2,
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    <strong style={{ fontWeight: 600 }}>
                      Table of Contents
                    </strong>
                  </Typography>
                  <List
                    sx={{ mb: 5, border: '1px solid #ccc', borderRadius: 2 }}
                  >
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#what" sx={{ color: '#0202ff' }}>
                          What is Form 990-N?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#formm990ez" sx={{ color: '#0202ff' }}>
                          What is Form 990-EZ?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#key" sx={{ color: '#0202ff' }}>
                          Key Difference of Form 990-N and Form 990-EZ
                        </Link>
                      </ListItemText>
                    </ListItem>

                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#common" sx={{ color: '#0202ff' }}>
                          Common Scenarios
                        </Link>
                      </ListItemText>
                    </ListItem>
                  </List>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="what"
                >
                  What is Form 990-N?
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }} component="ul">
                  <li style={{ marginBottom: 8 }}>
                    <span style={{ fontWeight: 600 }}> Form 990- N</span> is
                    also known as the{' '}
                    <span style={{ fontWeight: 600 }}> e-Postcard</span>, which
                    is for very small tax-exempt organizations with gross
                    receipts of{' '}
                    <span style={{ fontWeight: 600 }}>$50,000 or less</span>{' '}
                    annually.
                  </li>
                  <li>
                    It is simple and quick to complete; it requires only basic
                    information. No detailed financials or schedules.
                  </li>
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="formm990ez"
                >
                  What is Form 990-EZ?
                </Typography>
                <Box component="ul" sx={{ mb: 4, '& li': { mb: 1.2 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}> Form 990-EZ</span> is a
                    shorter version of Form 990 for organizations with gross
                    receipts between{' '}
                    <span style={{ fontWeight: 600 }}> $50,000</span> to
                    <span style={{ fontWeight: 600 }}> $200,000</span> and total
                    assets less than{' '}
                    <span style={{ fontWeight: 600 }}> $500,000.</span>
                  </li>
                  <li>
                    It requires more detailed financial and operational
                    information, including income/expense breakdowns and
                    schedules.{' '}
                    <Link
                      href="/resources/tax-exempt/form-990-ez-instructions/"
                      sx={{ color: '#0202ff' }}
                    >
                      {' '}
                      Form 990 EZ instructions
                    </Link>{' '}
                    provide step-by-step guidance to help organizations
                    accurately complete and file the return.
                  </li>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="key"
                >
                  Key Differences of Form 990-N vs Form 990-EZ
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Let&apos;s see the difference between of Form 990-N vs Form
                  990-EZ
                </Typography>
                <Box component="div" sx={{ mb: 4 }}>
                  <TableContainer
                    sx={{ border: '1px solid #828282', borderRadius: 2 }}
                  >
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell
                            sx={{
                              color: '#444',
                              width: 200,
                              borderRight: '1px solid #828282',
                              textAlign: 'center',
                              fontWeight: 600,
                              fontSize: 16,
                            }}
                          >
                            Features
                          </TableCell>

                          <TableCell
                            sx={{
                              color: '#444',
                              width: 200,
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                              fontWeight: 600,
                              fontSize: 16,
                            }}
                          >
                            Form 990-N
                          </TableCell>
                          <TableCell
                            sx={{
                              color: '#444',
                              width: 200,
                              textAlign: 'center',
                              fontWeight: 600,
                              fontSize: 16,
                            }}
                          >
                            Form 990-EZ
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                              fontWeight: 600,
                            }}
                          >
                            Filing requirements
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Gross receipts ≤{' '}
                            <span style={{ fontWeight: 600 }}>$50,000</span>
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center' }}>
                            Gross receipts{' '}
                            <span style={{ fontWeight: 600 }}>
                              $50,000 - $200,000;
                            </span>{' '}
                            <br />
                            Total assets{' '}
                            <span style={{ fontWeight: 600 }}>
                              &lt; $500,000
                            </span>
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                              fontWeight: 600,
                            }}
                          >
                            Filing method
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            It is filed only through electronic method
                            <br />
                            <span style={{ fontWeight: 600 }}>
                              (e-postcard)
                            </span>
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center' }}>
                            It is also filed electronically
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                              fontWeight: 600,
                            }}
                          >
                            Information required
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Basic information like EIN, tax year, legal name,
                            etc.
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                            }}
                          >
                            Along with basic information, detailed financial
                            statements, program details, compensation, etc.
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                              fontWeight: 600,
                            }}
                          >
                            Schedules
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Form 990-N does not require any{' '}
                            <span style={{ fontWeight: 600 }}>schedule</span>
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center' }}>
                            Form 990-EZ has
                            <span style={{ fontWeight: 600 }}>
                              {' '}
                              8 schedules{' '}
                            </span>
                            <ul style={{ fontWeight: 600, textAlign: 'left' }}>
                              <li>Schedule (A, B, C, E, G, L, N, O) </li>
                            </ul>
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                              fontWeight: 600,
                            }}
                          >
                            Extension
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            No extension for this <br />
                            Form 990-N.
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                            }}
                          >
                            It has six-month extension of{' '}
                            <Link
                              href="/tax-extension/efile-irs-form-8868-exempt-organization-tax-extension-online/"
                              sx={{ color: '#0202ff' }}
                            >
                              Form 8868
                            </Link>
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                              fontWeight: 600,
                            }}
                          >
                            Penalties
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            No penalties, but loss of tax-exempt status after{' '}
                            <span style={{ fontWeight: 600 }}> 3 years</span> of
                            non-filing.
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center' }}>
                            <span style={{ fontWeight: 600 }}> $25/day,</span>{' '}
                            and max{' '}
                            <span style={{ fontWeight: 600 }}>
                              {' '}
                              $12,500 or 5%
                            </span>{' '}
                            of gross receipts.
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                              fontWeight: 600,
                            }}
                          >
                            Voluntarily Filing Option
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            <span style={{ fontWeight: 600 }}> No</span> (cannot
                            file if eligible for{' '}
                            <span style={{ fontWeight: 600 }}>Form 990-EZ</span>
                            )
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                            }}
                          >
                            <span style={{ fontWeight: 600 }}>Yes</span> (
                            <span style={{ fontWeight: 600 }}>Form 990-N</span>{' '}
                            filers can choose{' '}
                            <span style={{ fontWeight: 600 }}>Form 990-EZ</span>{' '}
                            voluntarily)
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                              fontWeight: 600,
                            }}
                          >
                            Best for
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Very small nonprofit
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                            }}
                          >
                            Small to mid-sized nonprofits
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>

                <Box
                  sx={{
                    textAlign: 'center',
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: '#f9f9f9',
                    mb: 3,
                    border: '1px solid #ccc',
                  }}
                >
                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: { lg: 24, md: 20, sm: 18, xs: 16 },
                      fontWeight: 600,
                      mb: 0.5,
                      textAlign: 'left',
                    }}
                  >
                    Due Date{' '}
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 0, textAlign: 'left' }}>
                    Both <span style={{ fontWeight: 600 }}> Form 990-N</span>{' '}
                    and <span style={{ fontWeight: 600 }}>Form 990-EZ</span> are
                    due by the{' '}
                    <span style={{ fontWeight: 600 }}>
                      15th day of the 5th month
                    </span>{' '}
                    after the end of the organization’s fiscal year. For the
                    organizations with a calendar year-end of{' '}
                    <Hidden smDown>
                      {' '}
                      <br />
                    </Hidden>
                    <span style={{ fontWeight: 600 }}>December 31</span>, the
                    due date for the 2025 tax year is{' '}
                    <span style={{ fontWeight: 600 }}>May 15, 2026.</span>
                  </Typography>

                  {/* <Button
                    variant="contained"
                    color="primary"
                    sx={{
                      textTransform: 'none',
                      boxShadow: 'none',
                      borderRadius: 2,
                    }}
                    size="large"
                    href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=990duedates`}
                  >
                    File 8868 Now
                  </Button> */}
                </Box>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <strong style={{ fontWeight: 600 }}>Note:</strong> If the
                  deadline falls on the federal holiday or weekend, it is
                  extended to the next business day.
                </Alert>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="common"
                >
                  Common Scenarios
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mt: 1 }}>
                  1. My nonprofit’s annual income is $40,000. Which form should
                  I file?
                </Typography>
                <Typography variant="body1" sx={{ mt: 1, mb: 2 }}>
                  If your organization’s gross receipts are{' '}
                  <span style={{ fontWeight: 600 }}> $50,000 or less,</span>
                  you should file Form{' '}
                  <span style={{ fontWeight: 600 }}> 990-N (e-Postcard)</span>.
                  It’s quick and only requires basic information like your EIN,
                  tax year, and principal officer details
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mt: 1 }}>
                  2. Our receipts are $45,000, but we want to provide more
                  financial details. Can we file 990-EZ?
                </Typography>
                <Typography variant="body1" sx={{ mt: 1, mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Yes</span>, even if you
                  qualify for{' '}
                  <span style={{ fontWeight: 600 }}> Form 990-N</span>, you may
                  voluntarily choose to file Form{' '}
                  <span style={{ fontWeight: 600 }}> 990-EZ</span> for more
                  detailed reporting.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mt: 1 }}>
                  3. Our nonprofit made $120,000 last year and has $300,000 in
                  assets. What do we file?
                </Typography>
                <Typography variant="body1" sx={{ mt: 1, mb: 2 }}>
                  You must file{' '}
                  <span style={{ fontWeight: 600 }}> Form 990-EZ</span>. It’s
                  for organizations with gross receipts of{' '}
                  <span style={{ fontWeight: 600 }}> $50,000</span> but less
                  than <span style={{ fontWeight: 600 }}> $200,000</span> and
                  assets less than{' '}
                  <span style={{ fontWeight: 600 }}> $500,000</span>.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mt: 1 }}>
                  4. We have $100,000 in receipts but $600,000 in assets. Are we
                  eligible for 990-EZ?
                </Typography>
                <Typography variant="body1" sx={{ mt: 1, mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> No</span>. If your assets
                  are{' '}
                  <span style={{ fontWeight: 600 }}> $500,000 or more,</span>{' '}
                  you must file the full{' '}
                  <span style={{ fontWeight: 600 }}> Form 990</span>
                  instead of <span style={{ fontWeight: 600 }}> 990-EZ</span>.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mt: 1 }}>
                  5. Can we file both forms for the same year?
                </Typography>
                <Typography variant="body1" sx={{ mt: 1, mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> No</span>. You must choose
                  one form that fits your organization’s situation for each tax
                  year.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mt: 1 }}>
                  6. We just started our nonprofit this year. Do we need to
                  file?
                </Typography>
                <Typography variant="body1" sx={{ mt: 1, mb: 4 }}>
                  <span style={{ fontWeight: 600 }}> Yes</span>, most new
                  nonprofits must file a federal return for their first (even
                  partial) tax year. The form required depends on their gross
                  receipts for that period.
                </Typography>

                <Alert severity="info" icon={false} sx={{ borderRadius: 2 }}>
                  <Accordion
                    defaultExpanded
                    sx={{
                      '&.MuiAccordion-root::before, &.MuiAccordion-root.Mui-expanded::before':
                        {
                          background: 'none !important',
                        },
                      '&.MuiAccordion-root': { background: 'none !important' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls=""
                      sx={{
                        px: 0,
                        m: 0,
                        justifyContent: 'flex-start',
                        '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded':
                          { m: '0 !important', flexGrow: 0 },
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: '#014361',
                          fontSize: 16,
                        }}
                      >
                        Sources and References
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <Typography
                        component="div"
                        variant="body2"
                        sx={{ mt: 1 }}
                      >
                        The above information given is based on the IRS
                        guidelines. For more details, please refer to the
                        <Link
                          href="https://www.irs.gov/charities-non-profits/annual-electronic-notice-form-990-n-for-small-organizations-faqs-how-to-file"
                          target="_blank"
                          sx={{ color: '#0202ff' }}
                        >
                          {' '}
                          instructions for Form 990-N{' '}
                        </Link>
                        and{' '}
                        <Link
                          href="https://www.irs.gov/pub/irs-pdf/i990ez.pdf"
                          target="_blank"
                          sx={{ color: '#0202ff' }}
                        >
                          Form 990-EZ
                        </Link>
                        .
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </Alert>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 3,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              Still Not Sure Which Form to File?
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                fontWeight: 400,
                color: '#333333',
                mb: 2,
              }}
            >
              Whether your nonprofit qualifies for the quick and simple{' '}
              <span style={{ fontWeight: 600 }}> Form 990-N</span> or needs the
              more
              <br />
              detailed <span style={{ fontWeight: 600 }}>Form 990-EZ</span>,
              TaxZerone is here to help you file with confidence.
            </Typography>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=990Nvs990EZ`}
              >
                E-file Now!
              </Button>
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default Form990vsForm990EZ;
